import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Modal } from "react-bootstrap";
import { toast } from "sonner";
import withPermission from "../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";
import { FormEvent, useState } from "react";
import { verifyDiplome } from "../../api/diplome";

type Props = {
  show: boolean;
  onClose: () => void;
};

// Les statuts affichés côté interface restent volontairement simples.
// Toute la complexité de la réponse API est ramenée à l'un de ces cas.
type DiplomeStatus = "valid" | "duplicate" | "cancelled" | "not_found";

// Informations utiles à l'écran après vérification d'un diplôme.
// Les champs sont optionnels car un code introuvable ne renvoie presque rien.
type StudentDiplomeInfo = {
  status: DiplomeStatus;
  code: string;
  nom?: string;
  prenom?: string;
  diplome?: string;
  matricule?: string;
  numeroDiplome?: string;
  decision?: string;
  dateNaissance?: string;
  lieuNaissance?: string;
  classe?: string;
  promotion?: string;
  mention?: string;
  motifAnnulation?: string;
  observationAnnulation?: string;
};

const AuthenticateDiplomeModal = ({ show, onClose }: Props) => {
  const [reference, setReference] = useState("");
  const [result, setResult] = useState<StudentDiplomeInfo | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  // Nettoie la saisie au fur et à mesure et garde le format lisible en blocs.
  const formatReference = (value: string) => {
    const cleanValue = value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 16);

    return cleanValue.match(/.{1,4}/g)?.join("-") || "";
  };

  // L'interface est en français, donc les dates restent au format local attendu.
  const formatDate = (date?: string) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("fr-FR");
  };

  // Traduit les informations métier du back en statut clair pour l'utilisateur.
  const getDiplomeStatus = (type?: string, statut?: string): DiplomeStatus => {
    if (statut?.toLowerCase() === "annulé") return "cancelled";
    if (type === "DiplomaDuplicate") return "duplicate";
    return "valid";
  };

  // La réponse peut venir d'un diplôme original ou d'un duplicata.
  // On isole ici le bon objet avant de préparer les données affichées.
  const normalizeVerificationResult = (
    response: any,
    displayedCode: string,
  ): StudentDiplomeInfo => {
    const data = response?.data || response;
    const correspondance = data?.correspondance;
    const type = correspondance?.type;
    const rawDiplome = correspondance?.donnee;
    const diplomeData =
      type === "DiplomaDuplicate"
        ? rawDiplome?.Diploma || rawDiplome
        : rawDiplome;
    const student = diplomeData?.ParcoursStudent?.StudentProfil;
    const parcours = diplomeData?.ParcoursStudent?.Parcour;
    const cycle = parcours?.Cycle;
    const classe = diplomeData?.Class;
    const level = classe?.Level;
    const academicYear = level?.AcademicYear;
    const verificationCode = data?.verificationCode?.verification_code;

    // Si le back ne trouve aucune correspondance, on garde seulement le code saisi.
    if (!correspondance || !diplomeData) {
      return {
        status: "not_found",
        code: displayedCode,
      };
    }

    return {
      status: getDiplomeStatus(type, diplomeData?.statut),
      code: verificationCode
        ? formatReference(verificationCode)
        : displayedCode,
      nom: student?.lastname,
      prenom: student?.firstname,
      matricule: student?.matricule,
      diplome: cycle?.diploma_name || cycle?.label || level?.cycle,
      numeroDiplome: diplomeData?.number,
      decision: diplomeData?.Decision?.number,
      dateNaissance: formatDate(student?.birthday),
      lieuNaissance: student?.place_birth,
      classe: [level?.label, classe?.label].filter(Boolean).join(" - "),
      promotion: academicYear?.label,
      mention: diplomeData?.MentionCycle,
      motifAnnulation: diplomeData?.reason_for_cancellation,
      observationAnnulation: diplomeData?.observation_cancellation,
    };
  };

  // Remet la modale dans son état initial pour une nouvelle recherche.
  const resetAuthentication = () => {
    setReference("");
    setResult(null);
    setIsAuthenticating(false);
  };

  const handleClose = () => {
    resetAuthentication();
    onClose();
  };

  // Vérifie d'abord la forme du code, puis interroge le back avec la version sans tirets.
  const handleAuthenticate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalizedReference = reference.trim().toUpperCase();
    const verificationCode = normalizedReference.replace(/-/g, "");

    if (!normalizedReference) {
      toast.error("Veuillez saisir une référence de diplôme.");
      return;
    }

    if (
      !/^[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/.test(
        normalizedReference,
      )
    ) {
      toast.error("Le code doit respecter le format XXXX-XXXX-XXXX-XXXX.");
      return;
    }

    try {
      setIsAuthenticating(true);
      const response = await verifyDiplome(verificationCode, buildPermissionContext(Resource.DIPLOME_AUTH, Action.READ));
      // console.log("Résultat de la vérification du diplôme :", response);
      setResult(normalizeVerificationResult(response, normalizedReference));
    } catch (error: any) {
      // console.log(error)
      // Un 404 n'est pas une erreur bloquante ici : c'est un résultat à afficher.
      if (error?.response?.status === 404) {
        setResult({
          status: "not_found",
          code: normalizedReference,
        });
        return;
      }
      toast.error(error?.message);
    } finally {
      setIsAuthenticating(false);
    }

    // setResult(
    //   mockDiplomes[normalizedReference] || {
    //     status: "not_found",
    //     code: normalizedReference,
    //   },
    // );
  };

  // Centralise les libellés, icônes et couleurs pour garder le JSX plus lisible.
  const getResultDisplay = (status: DiplomeStatus) => {
    if (status === "valid") {
      return {
        title: "Diplôme authentique",
        description: "Ce diplôme existe et il est valide.",
        className: "border-success bg-success-transparent",
        icon: "ti ti-circle-check",
        iconClass: "text-success",
      };
    }

    if (status === "duplicate") {
      return {
        title: "Duplicata authentique",
        description:
          "Ce duplicata est valide et rattaché à un diplôme existant.",
        className: "border-primary bg-primary-transparent",
        icon: "ti ti-copy-check",
        iconClass: "text-primary",
      };
    }

    if (status === "cancelled") {
      return {
        title: "Diplôme annulé",
        description:
          "Ce diplôme existe, mais il a été annulé. Il ne doit plus être considéré comme valide.",
        className: "border-danger bg-danger-transparent",
        icon: "ti ti-circle-x",
        iconClass: "text-danger",
      };
    }

    return {
      title: "Diplôme introuvable",
      description:
        "Aucun diplôme ne correspond à ce code. Vérifiez la saisie ou contactez l'administration.",
      className: "border-warning bg-warning-transparent",
      icon: "ti ti-alert-triangle",
      iconClass: "text-warning",
    };
  };

  // Prévisualisation façon document officiel, utilisée seulement si le diplôme existe.
  const renderPdfPreview = (diplome: StudentDiplomeInfo) => {
    const isDuplicate = diplome.status === "duplicate";
    const isCancelled = diplome.status === "cancelled";
    const pdfStatus = getResultDisplay(diplome.status);
    const headerClass = isCancelled
      ? "bg-danger"
      : isDuplicate
        ? "bg-primary"
        : "bg-success";

    return (
      <div className="bg-white h-100">
        <div
          className={`p-4 border-bottom d-flex align-items-center justify-content-between text-white ${headerClass}`}
        >
          <div className="d-flex align-items-center gap-3">
            <div className={`avatar avatar-xl bg-white ${pdfStatus.iconClass}`}>
              <i className={`${pdfStatus.icon} fs-28`} />
            </div>
            <div>
              <h5 className="mb-0">{pdfStatus.title}</h5>
              <small>{pdfStatus.description}</small>
            </div>
          </div>
          <i className="ti ti-qrcode fs-36" />
        </div>

        <div className="px-5 py-4 text-center">
          <h3 className="mb-2">
            {diplome.prenom} {diplome.nom}
          </h3>
          <p className="text-muted mb-1">
            Matricule : <strong>{diplome.matricule || "-"}</strong>
          </p>
          <p className="text-muted mb-4">
            Né(e) le {diplome.dateNaissance || "-"} à{" "}
            {diplome.lieuNaissance || "-"}
          </p>
          <div className="mb-4">
            <div className="fw-semibold" style={{ fontSize: 18 }}>
              est diplômé(e) en
            </div>
            <div className="text-success fw-bold" style={{ fontSize: 34 }}>
              {diplome.diplome || "-"}
            </div>
          </div>

          <div className="row g-3 text-start my-2">
            <div className="col-md-6">
              <div className="border rounded p-3 bg-white">
                <small className="text-muted d-block">Classe</small>
                <strong>{diplome.classe || "-"}</strong>
              </div>
            </div>
            <div className="col-md-6">
              <div className="border rounded p-3 bg-white">
                <small className="text-muted d-block">Promotion</small>
                <strong>{diplome.promotion || "-"}</strong>
              </div>
            </div>
            <div className="col-md-6">
              <div className="border rounded p-3 bg-white">
                <small className="text-muted d-block">Mention</small>
                <strong>{diplome.mention || "-"}</strong>
              </div>
            </div>
            <div className="col-md-6">
              <div className="border rounded p-3 bg-white">
                <small className="text-muted d-block">Code</small>
                <strong>{diplome.code}</strong>
              </div>
            </div>
            <div className="col-md-6">
              <div className="border rounded p-3 bg-white">
                <small className="text-muted d-block">Numéro diplôme</small>
                <strong>{diplome.numeroDiplome || "-"}</strong>
              </div>
            </div>
            <div className="col-md-6">
              <div className="border rounded p-3 bg-white">
                <small className="text-muted d-block">Décision</small>
                <strong>{diplome.decision || "-"}</strong>
              </div>
            </div>
          </div>

          {isCancelled && (
            <div className="alert alert-danger text-start mt-4 mb-0">
              <div className="fw-semibold mb-1">Motif d'annulation</div>
              <div>{diplome.motifAnnulation || "-"}</div>
              {diplome.observationAnnulation && (
                <small className="d-block mt-2">
                  Observation : {diplome.observationAnnulation}
                </small>
              )}
            </div>
          )}
        </div>

        {isDuplicate && (
          <div
            className="position-absolute top-0 end-0 bg-info text-white px-5 py-2"
            style={{ transform: "translate(28%, 80%) rotate(45deg)" }}
          >
            <strong>DUPLICATA</strong>
          </div>
        )}

        {isCancelled && (
          <div className="position-absolute top-50 start-50 translate-middle">
            <div
              className="border border-danger text-danger fw-bold px-4 py-2"
              style={{
                transform: "rotate(-18deg)",
                fontSize: 42,
                letterSpacing: 2,
                opacity: 0.9,
              }}
            >
              ANNULE
            </div>
          </div>
        )}
      </div>
    );
  };

  const resultDisplay = result ? getResultDisplay(result.status) : null;

  return (
    <>
      <Modal show={show} size="xl" onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Authentifier un diplôme</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!result && (
            <form
              id="authenticate-diplome-form"
              onSubmit={handleAuthenticate}
              className="d-flex flex-column align-items-center justify-content-center py-5"
            >
              <div className="w-100" style={{ maxWidth: 520 }}>
                <label className="form-label text-center d-block">
                  Code d'authentification
                </label>
                <div className="input-group input-group-lg">
                  <span className="input-group-text">
                    <i className="ti ti-license" />
                  </span>
                  <input
                    type="text"
                    className="form-control text-center"
                    placeholder="XXXX-XXXX-XXXX-XXXX"
                    maxLength={19}
                    value={reference}
                    disabled={isAuthenticating}
                    onChange={(event) => {
                      setReference(formatReference(event.target.value));
                      setResult(null);
                    }}
                  />
                </div>
                <div className="text-center mt-4">
                  <button
                    type="submit"
                    className="btn btn-primary px-4"
                    disabled={isAuthenticating}
                  >
                    {isAuthenticating && (
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                    )}
                    {isAuthenticating ? "Authentification..." : "Authentifier"}
                  </button>
                </div>

                <div className="text-center mt-3">
                  <small className="text-muted">
                    Codes de test : DIPV-ALID-2026-0001, DIPD-UPLI-2026-0002,
                    DIPA-NNUL-2026-0003, ou un code inconnu.
                  </small>
                </div>
              </div>
            </form>
          )}

          {result && resultDisplay && (
            <div className="d-flex justify-content-center pb-4">
              <div
                className={`border rounded p-4 w-100 ${resultDisplay.className}`}
                style={{ maxWidth: 980 }}
              >
                {result.status !== "not_found" && renderPdfPreview(result)}

                {result.status === "not_found" && (
                  <div className="text-center py-4">
                    <i
                      className={`${resultDisplay.icon} ${resultDisplay.iconClass} fs-36`}
                    />
                    <h5 className="mt-3 mb-1">{resultDisplay.title}</h5>
                    <p className="text-muted mb-0">
                      {resultDisplay.description}
                    </p>
                    <span className="badge badge-soft-dark mt-3">
                      Code : {result.code}
                    </span>
                  </div>
                )}

                <div className="text-center mt-2">
                  <button
                    type="button"
                    className="btn btn-primary px-4"
                    onClick={resetAuthentication}
                  >
                    Authentifier un autre
                  </button>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={handleClose}
          >
            Annuler
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  AuthenticateDiplomeModal,
  [buildPermission(Resource.DIPLOME, Action.READ)],
  "masquer",
);
